import {removeTrapFocus, trapFocus} from '@theme/focus';
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

function profileEditName() {
  const profileEditNameModal = document.querySelector('.rs-profile-edit-name-modal');
  const profileEditNameButton = document.querySelector('.rs-profile-edit-name__button');
  const profileFirstNameInput = document.querySelector('#input-profile-edit-first-name');
  const profileLastNameInput = document.querySelector('#input-profile-edit-last-name');

  /** Shows the success banner (from Liquid), auto-hides after 5s or on close click. */
  function showProfileEditNameSuccessBanner() {
    const banner = document.querySelector('.rs-profile-edit-name-success-banner');
    if (!banner) return;

    banner.style.display = '';
    banner.classList.add('rs-banner--visible');

    const closeBtn = banner.querySelector('.rs-banner__close-button');
    const hideBanner = () => {
      banner.classList.remove('rs-banner--visible');
      banner.style.display = 'none';
    };

    closeBtn?.addEventListener('click', hideBanner, {once: true});
    setTimeout(hideBanner, 5000);
  }

  /**
   * Restore the last saved name and clear validation state.
   */
  function resetProfileEditNameForm() {
    [profileFirstNameInput, profileLastNameInput].forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      input.value = input.defaultValue;
      input.closest('.rs-input__content')?.classList.remove('rs-error');
    });
  }

  /**
   * Close the modal, release focus trap, and return focus to the trigger.
   */
  function closeProfileEditNameModal() {
    if (!profileEditNameModal || profileEditNameModal.classList.contains('rs-hidden')) return;

    removeTrapFocus();
    resetProfileEditNameForm();
    profileEditNameModal.setAttribute('aria-hidden', 'true');
    profileEditNameModal.classList.add('rs-hidden');
    if (profileEditNameButton instanceof HTMLElement) {
      profileEditNameButton.focus();
    }
  }

  /**
   * Open the modal and keep keyboard focus inside it.
   */
  function openProfileEditNameModal() {
    if (!profileEditNameModal) return;

    profileEditNameModal.classList.remove('rs-hidden');
    profileEditNameModal.setAttribute('aria-hidden', 'false');

    const modalContent = profileEditNameModal.querySelector('.rs-modal__content');
    if (!(modalContent instanceof HTMLElement)) return;

    const initialFocus =
      profileFirstNameInput instanceof HTMLElement && modalContent.contains(profileFirstNameInput)
        ? profileFirstNameInput
        : null;

    // Trap within the dialog panel only (not the full-screen overlay wrapper).
    requestAnimationFrame(() => {
      trapFocus(modalContent, {initialFocus});
    });
  }

  if (profileEditNameButton && profileEditNameModal) {
    profileEditNameButton.addEventListener('click', () => {
      openProfileEditNameModal();
    });
  }

  const profileEditNameSaveButton = document.querySelector('.rs-profile-edit-name-save-button');
  if (profileEditNameSaveButton) {
    profileEditNameSaveButton.addEventListener('click', async () => {
      const isValid = formValidation(profileFirstNameInput, profileLastNameInput);

      if (isValid) {
        const didUpdate = await runAPI();
        if (didUpdate) {
          if (profileFirstNameInput instanceof HTMLInputElement) {
            profileFirstNameInput.defaultValue = profileFirstNameInput.value;
          }
          if (profileLastNameInput instanceof HTMLInputElement) {
            profileLastNameInput.defaultValue = profileLastNameInput.value;
          }
          closeProfileEditNameModal();
          showProfileEditNameSuccessBanner();
        }
      }
    });
  }

  profileEditNameModal?.querySelectorAll('.rs-modal-close, .rs-modal__close-button').forEach((button) => {
    button.addEventListener('click', () => {
      closeProfileEditNameModal();
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!profileEditNameModal || profileEditNameModal.classList.contains('rs-hidden')) return;
    e.preventDefault();
    closeProfileEditNameModal();
  });
}

/**
 * @param {Element | null} profileFirstNameInput
 * @param {Element | null} profileLastNameInput
 */
function formValidation(profileFirstNameInput, profileLastNameInput) {
  const firstName = profileFirstNameInput?.value.trim();
  const lastName = profileLastNameInput?.value.trim();

  // Check if first name or last name is empty
  if (firstName === '') {
    profileFirstNameInput?.closest('.rs-input__content').classList.add('rs-error');
  } else {
    profileFirstNameInput?.closest('.rs-input__content').classList.remove('rs-error');
  }

  if (lastName === '') {
    profileLastNameInput?.closest('.rs-input__content').classList.add('rs-error');
  } else {
    profileLastNameInput?.closest('.rs-input__content').classList.remove('rs-error');
  }

  // Check if there is an error and return true/false
  if (profileFirstNameInput?.closest('.rs-input__content').classList.contains('rs-error') || profileLastNameInput?.closest('.rs-input__content').classList.contains('rs-error')) {
    return false;
  } else {
    return true;
  }
}

/**
 * Keeps header customer dropdown label in sync with Liquid format: `{button_text}, {firstName}`.
 * @param {string} firstName
 */
function updateCustomerDropdownButtonText(firstName) {
  document.querySelectorAll('.rs-customer-dropdown__button--text').forEach((el) => {
    const prefix = el.getAttribute('data-rs-customer-dropdown-prefix');
    if (prefix != null && prefix !== '') {
      el.textContent = `${prefix}, ${firstName}`;
    } else {
      el.textContent = firstName;
    }
  });
}

async function runAPI() {
  const profileFirstNameInput = document.querySelector('#input-profile-edit-first-name');
  const profileLastNameInput = document.querySelector('#input-profile-edit-last-name');
  const firstName = profileFirstNameInput?.value.trim();
  const lastName = profileLastNameInput?.value.trim();
  const customerId = document.body.getAttribute('data-customer-id');
  const profileNamePlaceholder = document.querySelector('.rs-profile-name__text');
  const token = window.myJWT;

  if (!firstName || !lastName) {
    console.error('First name and last name are required');
    return false;
  }

  if (!customerId) {
    console.error('Missing required data: customerId');
    return false;
  }

  try {
    const response = await fetch(`${getCustomerAppBaseUrl()}/api/customer/update-name?id=${customerId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        firstName: firstName,
        lastName: lastName,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Failed to update customer name:', errorData);
      return false;
    }

    // Update the profile name in the UI
    if (profileNamePlaceholder) {
      profileNamePlaceholder.textContent = `${firstName} ${lastName}`;
    }

    updateCustomerDropdownButtonText(firstName);
    return true;
  } catch (error) {
    console.error('Error updating customer name:', error);
    return false;
  }
}

document.addEventListener('DOMContentLoaded', profileEditName);
